import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Table,
} from "reactstrap";

import axios from "axios";
import swal from "sweetalert";

import { baseurl } from "../../../baseurl.js";
import { GetIp } from "../ShortCutComponents/Get_ip.jsx";
import ResponseLoader from "../ShortCutComponents/ResponseLoader.jsx";
import { checkPermission } from "../../../Helper.js";

// =========================================
// INITIAL STATES
// =========================================

const initialFormState = {
  tabledata: [],
  meat_qual_desc: "",
  manual_rate_yn: 0,
};

const initialEditState = {
  meat_qual_desc: "",
  manual_rate_yn: 0,
};

export default function MeatQualifier() {
  // =========================================
  // LOCAL STORAGE
  // =========================================

  const username = localStorage.getItem("username");
  const token = localStorage.getItem("token");
  const companyId = localStorage.getItem("companyId");

  // =========================================
  // HEADERS
  // =========================================

  const headers = useMemo(
    () => ({
      headers: {
        authorization: token,
      },
    }),
    [token]
  );

  // =========================================
  // STATES
  // =========================================

  const [loadingResponse, setLoadingResponse] =
    useState(false);

  const [editingId, setEditingId] =
    useState(null);

  const [data, setData] =
    useState(initialFormState);

  const [editData, setEditData] =
    useState(initialEditState);

  // =========================================
  // ALERT FUNCTION
  // =========================================

  const showAlert = (
    title,
    text,
    icon = "success"
  ) => {
    swal({
      title,
      text,
      icon,
    });
  };

  // =========================================
  // RESET FORM
  // =========================================

  const resetForm = () => {
    setData((prev) => ({
      ...prev,
      meat_qual_desc: "",
      manual_rate_yn: 0,
    }));
  };

  // =========================================
  // DUPLICATE CHECK
  // =========================================

  const isDuplicateEntry = ({
    meat_qual_desc,
    manual_rate_yn,
    excludeId = null,
  }) => {
    return data.tabledata.some(
      (row) =>
        row.meat_qual_desc
          ?.trim()
          .toLowerCase() ===
          meat_qual_desc
            ?.trim()
            .toLowerCase() &&
        Number(row.manual_rate_yn) ===
          Number(manual_rate_yn) &&
        row.meat_qual_id !== excludeId
    );
  };

  // =========================================
  // GET DATA
  // =========================================

  const getData = useCallback(async () => {
    try {
      const response = await axios.post(
        `${baseurl}/pointofsale/getMeatQualifier`,
        {
          companyId,
          username,
        },
        headers
      );

      setData((prev) => ({
        ...prev,
        tabledata:
          response?.data?.data || [],
      }));
    } catch (error) {
      console.log(error);

      showAlert(
        "Error",
        "Failed to load data.",
        "error"
      );
    }
  }, [companyId, username, headers]);

  // =========================================
  // INITIAL LOAD
  // =========================================

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoadingResponse(true);

        await checkPermission(
          "/meatQualifier",
          0
        );

        await getData();
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingResponse(false);
      }
    };

    initialize();
  }, [getData]);

  // =========================================
  // HANDLE SUBMIT
  // =========================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const meatQualDesc =
        data.meat_qual_desc?.trim();

      if (!meatQualDesc) {
        showAlert(
          "Validation",
          "Please enter Meat Qualifier Description.",
          "error"
        );

        return;
      }

      const duplicate = isDuplicateEntry({
        meat_qual_desc: meatQualDesc,
        manual_rate_yn: data.manual_rate_yn,
      });

      if (duplicate) {
        showAlert(
          "Duplicate Entry",
          "This record already exists.",
          "error"
        );

        return;
      }

      setLoadingResponse(true);

      const { userIp, userCountry } =
        await GetIp();

      const response = await axios.post(
        `${baseurl}/pointofsale/addMeatQualifier`,
        {
          companyId,
          username,
          userIp,
          userCountry,
          meat_qual_desc:
            meatQualDesc,
          manual_rate_yn:
            data.manual_rate_yn,
        },
        headers
      );

      if (response?.data?.duplicate) {
        showAlert(
          "Duplicate Entry",
          "Duplicate record is not allowed.",
          "error"
        );

        return;
      }

      showAlert(
        "Saved Successfully!",
        "Record added successfully."
      );

      resetForm();

      getData();
    } catch (error) {
      console.log(error);

      showAlert(
        "Error",
        "Something went wrong.",
        "error"
      );
    } finally {
      setLoadingResponse(false);
    }
  };

  // =========================================
  // HANDLE EDIT
  // =========================================

  const handleEdit = (row) => {
    setEditingId(row.meat_qual_id);

    setEditData({
      meat_qual_desc:
        row?.meat_qual_desc || "",
      manual_rate_yn: Number(
        row?.manual_rate_yn
      ),
    });
  };

  // =========================================
  // HANDLE UPDATE
  // =========================================

  const handleUpdate = async () => {
    try {
      const meatQualDesc =
        editData.meat_qual_desc?.trim();

      if (!meatQualDesc) {
        showAlert(
          "Validation",
          "Please enter Meat Qualifier Description.",
          "error"
        );

        return;
      }

      const duplicate = isDuplicateEntry({
        meat_qual_desc:
          meatQualDesc,
        manual_rate_yn:
          editData.manual_rate_yn,
        excludeId: editingId,
      });

      if (duplicate) {
        showAlert(
          "Duplicate Entry",
          "This record already exists.",
          "error"
        );

        return;
      }
      setLoadingResponse(true)

      await axios.post(
        `${baseurl}/pointofsale/updateMeatQualifier`,
        {
          companyId,
          username,
          id: editingId,
          meat_qual_desc:
            meatQualDesc,
          manual_rate_yn:
            editData.manual_rate_yn,
        },
        headers
      );

      showAlert(
        "Updated!",
        "Record updated successfully."
      );

      setEditingId(null);

      setEditData(
        initialEditState
      );

      getData();
    } catch (error) {
      console.log(error);

      showAlert(
        "Error",
        "Something went wrong.",
        "error"
      );
    } finally {
      setLoadingResponse(false);
    }
  };

  // =========================================
  // HANDLE DELETE
  // =========================================

  const handleDelete = (
    meat_qual_id
  ) => {
    swal({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;

      try {
        await axios.post(
          `${baseurl}/pointofsale/deleteMeatQualifier`,
          {
            companyId,
            username,
            id: meat_qual_id,
          },
          headers
        );

        showAlert(
          "Deleted!",
          "Record deleted successfully."
        );

        getData();
      } catch (error) {
        console.log(error);

        showAlert(
          "Error",
          "Something went wrong.",
          "error"
        );
      }
    });
  };

  // =========================================
  // LOADER
  // =========================================

  if (loadingResponse) {
    return <ResponseLoader />;
  }

  // =========================================
  // JSX
  // =========================================

  return (
    <div className="container-fluid bg-white">
      {/* HEADER */}
      <div className="row">
        <div className="col-md-12 h3 p-3">
          Meat Qualifier
        </div>
      </div>

      {/* FORM */}
      <Form onSubmit={handleSubmit}>
        <div className="row">
          {/* MEAT QUALIFIER DESC */}
          <div className="col-md-6 mb-3">
            <Label className="font-weight-bold">
              Meat Qualifier Description
            </Label>

            <Input
              type="text"
              value={
                data.meat_qual_desc
              }
              onChange={({
                target,
              }) =>
                setData((prev) => ({
                  ...prev,
                  meat_qual_desc:
                    target?.value ||
                    "",
                }))
              }
              name="meat_qual_desc"
              id="meat_qual_desc"
              placeholder="Enter Meat Qualifier Description"
            />
          </div>

          {/* MANUAL RATE */}
          <div className="col-md-6 mb-3">
            <Label className="font-weight-bold">
              Manual Rate
            </Label>

            <div className="d-flex align-items-center pt-2">
              <label className="mr-4">
                <input
                  type="radio"
                  checked={
                    data.manual_rate_yn ===
                    1
                  }
                  onChange={() =>
                    setData((prev) => ({
                      ...prev,
                      manual_rate_yn: 1,
                    }))
                  }
                />

                <span className="ml-1">
                  Yes
                </span>
              </label>

              <label>
                <input
                  type="radio"
                  checked={
                    data.manual_rate_yn ===
                    0
                  }
                  onChange={() =>
                    setData((prev) => ({
                      ...prev,
                      manual_rate_yn: 0,
                    }))
                  }
                />

                <span className="ml-1">
                  No
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* BUTTONS */}
        <FormGroup>
          <Button
            type="submit"
            className="mr-2 invoiceSubmitButton"
          >
            Save
          </Button>

          <Button
            type="button"
            onClick={resetForm}
          >
            Cancel
          </Button>
        </FormGroup>
      </Form>

      {/* TABLE */}
      <Table
        bordered
        striped
        className="mt-4"
      >
        <thead className="rpr_header">
          <tr>
            <th>
              Meat Qualifier
              Description
            </th>

            <th>
              Manual Rate
            </th>

            <th width="180">
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          {data.tabledata.length >
          0 ? (
            data.tabledata.map(
              (row) => (
                <tr
                  key={
                    row.meat_qual_id
                  }
                >
                  {/* DESCRIPTION */}
                  <td>
                    {editingId ===
                    row.meat_qual_id ? (
                      <Input
                        type="text"
                        value={
                          editData.meat_qual_desc
                        }
                        onChange={({
                          target,
                        }) =>
                          setEditData(
                            (
                              prev
                            ) => ({
                              ...prev,
                              meat_qual_desc:
                                target?.value ||
                                "",
                            })
                          )
                        }
                      />
                    ) : (
                      row.meat_qual_desc
                    )}
                  </td>

                  {/* MANUAL RATE */}
                  <td>
                    {editingId ===
                    row.meat_qual_id ? (
                      <div className="d-flex">
                        <label className="mr-4">
                          <input
                            type="radio"
                            checked={
                              editData.manual_rate_yn ===
                              1
                            }
                            onChange={() =>
                              setEditData(
                                (
                                  prev
                                ) => ({
                                  ...prev,
                                  manual_rate_yn: 1,
                                })
                              )
                            }
                          />

                          <span className="ml-1">
                            Yes
                          </span>
                        </label>

                        <label>
                          <input
                            type="radio"
                            checked={
                              editData.manual_rate_yn ===
                              0
                            }
                            onChange={() =>
                              setEditData(
                                (
                                  prev
                                ) => ({
                                  ...prev,
                                  manual_rate_yn: 0,
                                })
                              )
                            }
                          />

                          <span className="ml-1">
                            No
                          </span>
                        </label>
                      </div>
                    ) : Number(
                        row.manual_rate_yn
                      ) === 1 ? (
                      "Yes"
                    ) : (
                      "No"
                    )}
                  </td>

                  {/* ACTIONS */}
                  <td>
                    <Button
                      color="danger"
                      size="sm"
                      className="mr-2"
                      onClick={() =>
                        handleDelete(
                          row.meat_qual_id
                        )
                      }
                    >
                      Delete
                    </Button>

                    {editingId ===
                    row.meat_qual_id ? (
                      <Button
                        color="success"
                        size="sm"
                        onClick={
                          handleUpdate
                        }
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() =>
                          handleEdit(
                            row
                          )
                        }
                      >
                        Edit
                      </Button>
                    )}
                  </td>
                </tr>
              )
            )
          ) : (
            <tr>
              <td
                colSpan="3"
                className="text-center"
              >
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}